import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { API_BASE_URL, apiClient } from '@/services/api-client';
import { useAuth } from '@/hooks/useAuth';
import type { BusinessLine, CostKind } from '../format';

export interface OperationsSummaryRow {
    businessLine: BusinessLine | string;
    incomeBaseCents: number;
    vatCents: number;
    suplidoCents: number;
    collectedCents: number;
    expenseBaseCents: number;
    expenseVatCents: number;
    expenseTotalCents: number;
    marginCents: number;
}

export interface EmittedInvoice {
    id: string;
    invoiceNumber: string;
    series: string;
    documentType: string;
    status: string;
    sourceType: string;
    businessLine: string;
    customerName: string;
    customerEmail: string;
    customerTaxId: string | null;
    issuedAt: string;
    pdfGeneratedAt: string | null;
    storeOrderNumber: string | null;
    incomeBaseCents: number;
    vatCents: number;
    suplidoCents: number;
    collectedCents: number;
}

export interface ReceivedAllocation {
    businessLine: BusinessLine;
    costKind: CostKind;
    taxBaseCents: number;
    taxAmountCents: number;
    totalCents: number;
}

export interface ReceivedInvoice {
    id: string;
    supplierName: string;
    supplierTaxId: string | null;
    invoiceNumber: string;
    issuedOn: string;
    taxBaseCents: number;
    taxAmountCents: number;
    totalCents: number;
    notes: string | null;
    hasPdf: boolean;
    allocations: ReceivedAllocation[];
}

export interface IssuedExportLine {
    invoiceNumber: string;
    series: string;
    documentType: string;
    issuedOn: string;
    businessLine: string;
    issuerLegalName: string;
    issuerTaxId: string;
    customerName: string;
    customerTaxId: string | null;
    customerEmail: string;
    customerType: string;
    addressLine: string | null;
    postalCode: string | null;
    city: string | null;
    province: string | null;
    country: string | null;
    lineIndex: number;
    description: string;
    quantity: number;
    taxRateBps: number;
    taxBaseCents: number;
    taxAmountCents: number;
    lineTotalCents: number;
    isSuplido: boolean;
    rectifiedInvoiceNumber: string | null;
    orderReference: string | null;
}

export interface OperationsExportData {
    lines: IssuedExportLine[];
    received: ReceivedInvoice[];
    disbursements: LicenseDisbursement[];
    summary: OperationsSummaryRow[];
    truncated: boolean;
}

export interface LicenseDisbursement {
    orderId: string;
    publicId: string;
    contactEmail: string;
    customerName: string | null;
    customerTaxId: string | null;
    status: string;
    officialFeeCents: number;
    officialFeePaidCents: number | null;
    authorityPaidAt: string | null;
    hasReceipt: boolean;
    invoiceNumber: string | null;
    paidAt: string | null;
}

function num(value: unknown): number {
    return Number(value ?? 0);
}

function asRecord(row: unknown): Record<string, unknown> {
    return (row ?? {}) as Record<string, unknown>;
}

export function useOperationsSummary(from: string, to: string) {
    const { user } = useAuth();
    return useQuery<OperationsSummaryRow[]>({
        queryKey: ['admin', 'operations-summary', from, to],
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_operations_summary', {
                p_admin_user_id: user!.id,
                p_date_from: from,
                p_date_to: to,
            });
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data).map((row) => {
                const r = asRecord(row);
                return {
                    businessLine: String(r.business_line ?? r.businessLine ?? ''),
                    incomeBaseCents: num(r.income_base_cents ?? r.incomeBaseCents),
                    vatCents: num(r.vat_cents ?? r.vatCents),
                    suplidoCents: num(r.suplido_cents ?? r.suplidoCents),
                    collectedCents: num(r.collected_cents ?? r.collectedCents),
                    expenseBaseCents: num(r.expense_base_cents ?? r.expenseBaseCents),
                    expenseVatCents: num(r.expense_vat_cents ?? r.expenseVatCents),
                    expenseTotalCents: num(r.expense_total_cents ?? r.expenseTotalCents),
                    marginCents: num(r.margin_cents ?? r.marginCents),
                };
            });
        },
    });
}

export function useEmittedInvoices(from: string, to: string, line: string, search: string) {
    const { user } = useAuth();
    return useQuery<EmittedInvoice[]>({
        queryKey: ['admin', 'operations-emitted', from, to, line, search],
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const params: Record<string, unknown> = {
                p_admin_user_id: user!.id,
                p_date_from: from,
                p_date_to: to,
                p_limit: 500,
            };
            if (line) params.p_business_line = line;
            if (search.trim()) params.p_search = search.trim();
            const { data, error } = await dbClient.rpc('admin_operations_emitted_invoices', params);
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data).map((row) => {
                const r = asRecord(row);
                return {
                    id: String(r.id),
                    invoiceNumber: String(r.invoice_number ?? ''),
                    series: String(r.series ?? ''),
                    documentType: String(r.document_type ?? 'invoice'),
                    status: String(r.status ?? ''),
                    sourceType: String(r.source_type ?? ''),
                    businessLine: String(r.business_line ?? ''),
                    customerName: String(r.customer_name ?? ''),
                    customerEmail: String(r.customer_email ?? ''),
                    customerTaxId: r.customer_tax_id != null && String(r.customer_tax_id) !== ''
                        ? String(r.customer_tax_id)
                        : null,
                    issuedAt: String(r.issued_at ?? ''),
                    pdfGeneratedAt: r.pdf_generated_at != null ? String(r.pdf_generated_at) : null,
                    storeOrderNumber: r.store_order_number != null ? String(r.store_order_number) : null,
                    incomeBaseCents: num(r.income_base_cents),
                    vatCents: num(r.vat_cents),
                    suplidoCents: num(r.suplido_cents),
                    collectedCents: num(r.collected_cents),
                };
            });
        },
    });
}

function parseAllocations(raw: unknown): ReceivedAllocation[] {
    const list = Array.isArray(raw) ? raw : [];
    return list.map((item) => {
        const r = asRecord(item);
        return {
            businessLine: String(r.business_line ?? 'store') as BusinessLine,
            costKind: String(r.cost_kind ?? 'stripe_fee') as CostKind,
            taxBaseCents: num(r.tax_base_cents),
            taxAmountCents: num(r.tax_amount_cents),
            totalCents: num(r.total_cents),
        };
    });
}

export function useReceivedInvoices(from: string, to: string) {
    const { user } = useAuth();
    return useQuery<ReceivedInvoice[]>({
        queryKey: ['admin', 'operations-received', from, to],
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_received_invoices', {
                p_admin_user_id: user!.id,
                p_date_from: from,
                p_date_to: to,
            });
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data).map((row) => {
                const r = asRecord(row);
                const issued = r.issued_on ?? r.issuedOn ?? '';
                return {
                    id: String(r.id),
                    supplierName: String(r.supplier_name ?? ''),
                    supplierTaxId: r.supplier_tax_id != null ? String(r.supplier_tax_id) : null,
                    invoiceNumber: String(r.invoice_number ?? ''),
                    issuedOn: String(issued).slice(0, 10),
                    taxBaseCents: num(r.tax_base_cents),
                    taxAmountCents: num(r.tax_amount_cents),
                    totalCents: num(r.total_cents),
                    notes: r.notes != null ? String(r.notes) : null,
                    hasPdf: Boolean(r.has_pdf),
                    allocations: parseAllocations(r.allocations),
                };
            });
        },
    });
}

export function useUpsertReceivedInvoice() {
    const { user } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            id?: string | null;
            supplierName: string;
            supplierTaxId: string;
            invoiceNumber: string;
            issuedOn: string;
            notes: string;
            allocations: ReceivedAllocation[];
        }) => {
            const { data, error } = await dbClient.rpc('admin_upsert_received_invoice', {
                p_admin_user_id: user!.id,
                p_id: input.id ?? null,
                p_supplier_name: input.supplierName,
                p_supplier_tax_id: input.supplierTaxId || null,
                p_invoice_number: input.invoiceNumber,
                p_issued_on: input.issuedOn,
                p_notes: input.notes || null,
                p_allocations: input.allocations.map((a) => ({
                    business_line: a.businessLine,
                    cost_kind: a.costKind,
                    tax_base_cents: a.taxBaseCents,
                    tax_amount_cents: a.taxAmountCents,
                    total_cents: a.taxBaseCents + a.taxAmountCents,
                })),
            });
            if (error) throw new Error(error.message);
            return data;
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['admin', 'operations-received'] });
            void qc.invalidateQueries({ queryKey: ['admin', 'operations-summary'] });
        },
    });
}

export function useDeleteReceivedInvoice() {
    const { user } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const { error } = await dbClient.rpc('admin_delete_received_invoice', {
                p_admin_user_id: user!.id,
                p_id: id,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['admin', 'operations-received'] });
            void qc.invalidateQueries({ queryKey: ['admin', 'operations-summary'] });
        },
    });
}

export function useLicenseDisbursements(from: string, to: string) {
    const { user } = useAuth();
    return useQuery<LicenseDisbursement[]>({
        queryKey: ['admin', 'operations-disbursements', from, to],
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_license_disbursements', {
                p_admin_user_id: user!.id,
                p_date_from: from,
                p_date_to: to,
            });
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data).map((row) => {
                const r = asRecord(row);
                const paid = r.official_fee_paid_cents;
                return {
                    orderId: String(r.order_id),
                    publicId: String(r.public_id ?? ''),
                    contactEmail: String(r.contact_email ?? ''),
                    customerName: r.customer_name != null && String(r.customer_name) !== ''
                        ? String(r.customer_name)
                        : null,
                    customerTaxId: r.customer_tax_id != null && String(r.customer_tax_id) !== ''
                        ? String(r.customer_tax_id)
                        : null,
                    status: String(r.status ?? ''),
                    officialFeeCents: num(r.official_fee_cents),
                    officialFeePaidCents: paid == null ? null : num(paid),
                    authorityPaidAt: r.authority_paid_at != null ? String(r.authority_paid_at).slice(0, 10) : null,
                    hasReceipt: Boolean(r.has_receipt),
                    invoiceNumber: r.invoice_number != null ? String(r.invoice_number) : null,
                    paidAt: r.paid_at != null ? String(r.paid_at) : null,
                };
            });
        },
    });
}

export function useSetLicenseDisbursement() {
    const { user } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { orderId: string; paidCents: number | null; paidOn: string | null }) => {
            const { error } = await dbClient.rpc('admin_set_license_disbursement', {
                p_admin_user_id: user!.id,
                p_order_id: input.orderId,
                p_paid_cents: input.paidCents,
                p_paid_on: input.paidOn,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['admin', 'operations-disbursements'] });
        },
    });
}

function textOrNull(value: unknown): string | null {
    if (value == null) return null;
    const text = String(value).trim();
    return text === '' ? null : text;
}

function mapIssuedExportLine(row: unknown): IssuedExportLine {
    const r = asRecord(row);
    const issued = r.issued_on ?? r.issuedOn ?? '';
    return {
        invoiceNumber: String(r.invoice_number ?? ''),
        series: String(r.series ?? ''),
        documentType: String(r.document_type ?? 'invoice'),
        issuedOn: String(issued).slice(0, 10),
        businessLine: String(r.business_line ?? ''),
        issuerLegalName: String(r.issuer_legal_name ?? ''),
        issuerTaxId: String(r.issuer_tax_id ?? ''),
        customerName: String(r.customer_name ?? ''),
        customerTaxId: textOrNull(r.customer_tax_id),
        customerEmail: String(r.customer_email ?? ''),
        customerType: String(r.customer_type ?? 'individual'),
        addressLine: textOrNull(r.address_line),
        postalCode: textOrNull(r.postal_code),
        city: textOrNull(r.city),
        province: textOrNull(r.province),
        country: textOrNull(r.country),
        lineIndex: num(r.line_index),
        description: String(r.description ?? ''),
        quantity: num(r.quantity),
        taxRateBps: num(r.tax_rate_bps),
        taxBaseCents: num(r.tax_base_cents),
        taxAmountCents: num(r.tax_amount_cents),
        lineTotalCents: num(r.line_total_cents),
        isSuplido: Boolean(r.is_suplido),
        rectifiedInvoiceNumber: textOrNull(r.rectified_invoice_number),
        orderReference: textOrNull(r.order_reference),
    };
}

export async function fetchOperationsExport(userId: string, from: string, to: string): Promise<OperationsExportData> {
    const [linesRes, receivedRes, disbursementsRes, summaryRes] = await Promise.all([
        dbClient.rpc('admin_operations_export_issued_lines', {
            p_admin_user_id: userId,
            p_date_from: from,
            p_date_to: to,
        }),
        dbClient.rpc('admin_list_received_invoices', {
            p_admin_user_id: userId,
            p_date_from: from,
            p_date_to: to,
        }),
        dbClient.rpc('admin_list_license_disbursements', {
            p_admin_user_id: userId,
            p_date_from: from,
            p_date_to: to,
        }),
        dbClient.rpc('admin_operations_summary', {
            p_admin_user_id: userId,
            p_date_from: from,
            p_date_to: to,
        }),
    ]);
    const failed = [linesRes, receivedRes, disbursementsRes, summaryRes].find((res) => res.error);
    if (failed?.error) throw new Error(failed.error.message);

    const lines = normalizeRpcResponse(linesRes.data).map(mapIssuedExportLine);
    const received = normalizeRpcResponse(receivedRes.data).map((row) => {
        const r = asRecord(row);
        const issued = r.issued_on ?? r.issuedOn ?? '';
        return {
            id: String(r.id),
            supplierName: String(r.supplier_name ?? ''),
            supplierTaxId: textOrNull(r.supplier_tax_id),
            invoiceNumber: String(r.invoice_number ?? ''),
            issuedOn: String(issued).slice(0, 10),
            taxBaseCents: num(r.tax_base_cents),
            taxAmountCents: num(r.tax_amount_cents),
            totalCents: num(r.total_cents),
            notes: textOrNull(r.notes),
            hasPdf: Boolean(r.has_pdf),
            allocations: parseAllocations(r.allocations),
        };
    });
    const disbursements = normalizeRpcResponse(disbursementsRes.data).map((row) => {
        const r = asRecord(row);
        const paid = r.official_fee_paid_cents;
        return {
            orderId: String(r.order_id),
            publicId: String(r.public_id ?? ''),
            contactEmail: String(r.contact_email ?? ''),
            customerName: textOrNull(r.customer_name),
            customerTaxId: textOrNull(r.customer_tax_id),
            status: String(r.status ?? ''),
            officialFeeCents: num(r.official_fee_cents),
            officialFeePaidCents: paid == null ? null : num(paid),
            authorityPaidAt: r.authority_paid_at != null ? String(r.authority_paid_at).slice(0, 10) : null,
            hasReceipt: Boolean(r.has_receipt),
            invoiceNumber: textOrNull(r.invoice_number),
            paidAt: r.paid_at != null ? String(r.paid_at) : null,
        };
    });
    const summary = normalizeRpcResponse(summaryRes.data).map((row) => {
        const r = asRecord(row);
        return {
            businessLine: String(r.business_line ?? r.businessLine ?? ''),
            incomeBaseCents: num(r.income_base_cents ?? r.incomeBaseCents),
            vatCents: num(r.vat_cents ?? r.vatCents),
            suplidoCents: num(r.suplido_cents ?? r.suplidoCents),
            collectedCents: num(r.collected_cents ?? r.collectedCents),
            expenseBaseCents: num(r.expense_base_cents ?? r.expenseBaseCents),
            expenseVatCents: num(r.expense_vat_cents ?? r.expenseVatCents),
            expenseTotalCents: num(r.expense_total_cents ?? r.expenseTotalCents),
            marginCents: num(r.margin_cents ?? r.marginCents),
        };
    });

    return { lines, received, disbursements, summary, truncated: lines.length >= 10000 };
}

async function authFetch(path: string, init?: RequestInit): Promise<Response> {
    const session = apiClient.getSession();
    if (!session?.access_token) throw new Error('Sesión no válida');
    const res = await fetch(`${API_BASE_URL}${path}`, {
        ...init,
        headers: {
            Authorization: `Bearer ${session.access_token}`,
            ...(init?.headers ?? {}),
        },
    });
    return res;
}

export async function uploadOpsPdf(kind: 'received' | 'disbursement', id: string, file: File): Promise<void> {
    const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
    const pdfBase64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
    const res = await authFetch(`/billing/admin/ops-doc/${kind}/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pdfBase64 }),
    });
    if (!res.ok) {
        let msg = 'No se pudo guardar el PDF';
        try {
            const err = await res.json();
            msg = err?.message || err?.error || msg;
        } catch { /* ignore */ }
        throw new Error(msg);
    }
}

export async function downloadOpsPdf(kind: 'received' | 'disbursement', id: string, filename: string): Promise<void> {
    const res = await authFetch(`/billing/admin/ops-doc/${kind}/${id}`);
    if (!res.ok) throw new Error('No se pudo descargar el PDF');
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
}
